/**
 * useSharedConversation - 共享对话处理 Hook
 * 提取自 ChatPage.tsx，处理共享对话的加载、显示和版本切换
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Message, Conversation } from '@/types';
import {
  rebuildMessagesWithVersionPath,
  fetchSharedConversationDetails,
} from '@/services/conversationService';

export interface SharedConversationData {
  conversationId: string;
  title: string;
  treeData: any[];
  versionPath: Record<number, number>;
  messages: Message[];
}

export interface UseSharedConversationOptions {
  isSharedRecordView: boolean;
  sharedConversationRecordId: number | null;
  currentConversation: Conversation | null;
  pendingConversationId: string | undefined;
}

export interface UseSharedConversationResult {
  sharedConversationData: SharedConversationData | null;
  sharedLoadError: string | null;
  isSharedLoading: boolean;
  displayConversation: Conversation | null;
  isSwitchingConversation: boolean;
  handleChangeChoiceForSharedTree: (
    depth: number,
    newVersionIndex: number
  ) => void;
  handleChangeChoiceForSharedMessages: (
    messageId: string,
    newActiveChoice: number
  ) => void;
}

export function useSharedConversation({
  isSharedRecordView,
  sharedConversationRecordId,
  currentConversation,
  pendingConversationId,
}: UseSharedConversationOptions): UseSharedConversationResult {
  const [sharedConversationData, setSharedConversationData] =
    useState<SharedConversationData | null>(null);
  const [sharedLoadError, setSharedLoadError] = useState<string | null>(null);
  const [isSharedLoading, setIsSharedLoading] = useState(false);
  const loadingSharedRecordIdRef = useRef<number | null>(null);

  // 加载共享对话详情
  useEffect(() => {
    if (!isSharedRecordView) {
      setSharedConversationData(null);
      setSharedLoadError(null);
      setIsSharedLoading(false);
      loadingSharedRecordIdRef.current = null;
      return;
    }

    const currentRecordId = sharedConversationRecordId as number;

    // 如果已经在加载同一个 sharedConversationRecordId，则跳过
    if (loadingSharedRecordIdRef.current === currentRecordId) {
      return;
    }
    loadingSharedRecordIdRef.current = currentRecordId;

    setIsSharedLoading(true);
    setSharedLoadError(null);

    (async () => {
      try {
        const details = await fetchSharedConversationDetails(currentRecordId);
        if (loadingSharedRecordIdRef.current !== currentRecordId) return;

        const messages = rebuildMessagesWithVersionPath(
          details.treeData,
          details.conversationId,
          details.versionPath
        );
        setSharedConversationData({
          ...details,
          messages,
        });
      } catch (e) {
        if (loadingSharedRecordIdRef.current !== currentRecordId) return;
        setSharedLoadError(
          e instanceof Error ? e.message : 'Failed to load shared conversation'
        );
        loadingSharedRecordIdRef.current = null;
      } finally {
        if (loadingSharedRecordIdRef.current === currentRecordId) {
          setIsSharedLoading(false);
        }
      }
    })();
  }, [isSharedRecordView, sharedConversationRecordId]);

  // 切换共享 tree 的版本
  const handleChangeChoiceForSharedTree = useCallback(
    (depth: number, newVersionIndex: number) => {
      setSharedConversationData(prev => {
        if (!prev) return prev;
        const previousPath = prev.versionPath || {};

        const newVersionPath: Record<number, number> = {};
        Object.keys(previousPath).forEach(key => {
          const keyDepth = parseInt(key, 10);
          if (keyDepth < depth) {
            newVersionPath[keyDepth] = previousPath[keyDepth];
          }
        });
        newVersionPath[depth] = newVersionIndex;

        const newMessages = rebuildMessagesWithVersionPath(
          prev.treeData,
          prev.conversationId,
          newVersionPath
        );

        return {
          ...prev,
          versionPath: newVersionPath,
          messages: newMessages,
        };
      });
    },
    []
  );

  // 切换共享 messages 的版本
  const handleChangeChoiceForSharedMessages = useCallback(
    (messageId: string, newActiveChoice: number) => {
      setSharedConversationData(prev => {
        if (!prev) return prev;
        if (prev.treeData && prev.treeData.length > 0) {
          return prev;
        }
        const messages = prev.messages || [];
        return {
          ...prev,
          messages: messages.map(msg =>
            msg.id === messageId
              ? { ...msg, activeChoice: newActiveChoice }
              : msg
          ),
        };
      });
    },
    []
  );

  // 计算显示的对话数据
  const displayConversation: Conversation | null = isSharedRecordView
    ? sharedConversationData
      ? ({
          ...sharedConversationData,
          id: sharedConversationData.conversationId,
          conversationId: sharedConversationData.conversationId,
          title: sharedConversationData.title,
          treeData: sharedConversationData.treeData,
          versionPath: sharedConversationData.versionPath,
          messages: sharedConversationData.messages,
          isLoading: false,
        } as Conversation)
      : null
    : currentConversation;

  // 判断是否在切换对话
  const isSwitchingConversation = isSharedRecordView
    ? isSharedLoading
    : !!pendingConversationId;

  return {
    sharedConversationData,
    sharedLoadError,
    isSharedLoading,
    displayConversation,
    isSwitchingConversation,
    handleChangeChoiceForSharedTree,
    handleChangeChoiceForSharedMessages,
  };
}

export default useSharedConversation;
